import { useState } from "react";
import { Anchor, ArrowLeft, User, Phone } from "lucide-react";
import api, { setCrewSession } from "@/lib/api";
import { toast } from "sonner";

export default function StaffSignInForm({ onSignedIn, onBack }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!name.trim() || phone.replace(/\D/g, "").length < 7) {
      setError("Please enter your full name and a valid phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/crew/sign-in", { name: name.trim(), phone: phone.trim() });
      setCrewSession(res.data.token, { crew_id: res.data.crew_id, name: res.data.name });
      toast.success(`Welcome, ${res.data.name}!`);
      onSignedIn && onSignedIn(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen lock-bg flex items-center justify-center p-4 sm:p-8" data-testid="staff-sign-in-screen">
      <form onSubmit={submit} className="w-full max-w-md glass rounded-3xl p-7 sm:p-9">
        <button type="button" onClick={onBack} className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800 font-semibold inline-flex items-center gap-1 mb-4" data-testid="staff-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png" alt="Rolling River Day Camp" className="w-12 h-12 rounded-2xl object-contain bg-white shadow-md p-1" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Rolling River Day Camp</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Staff Sign In</h1>
          </div>
        </div>

        <p className="text-sm text-slate-600 mb-6">
          Sign in with your name and phone to record today's arrival, sign out at the end of the day, or submit your planned-absence form.
        </p>

        <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold flex items-center gap-1.5 mb-1">
          <User className="w-3 h-3" /> Full Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Jane Smith"
          autoFocus
          className="w-full px-4 py-3 rounded-xl bg-white/95 border border-slate-200 mb-4 outline-none focus:ring-2 focus:ring-blue-400"
          data-testid="staff-name-input"
        />

        <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold flex items-center gap-1.5 mb-1">
          <Phone className="w-3 h-3" /> Phone Number
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
          className="w-full px-4 py-3 rounded-xl bg-white/95 border border-slate-200 mb-4 outline-none focus:ring-2 focus:ring-blue-400 tabular-nums"
          data-testid="staff-phone-input"
        />

        {error && (
          <div className="mb-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="staff-signin-error">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-touch rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition disabled:opacity-50 px-4 py-3 text-base"
          data-testid="staff-signin-submit"
        >
          {loading ? "Signing in…" : "Sign In →"}
        </button>

        <div className="mt-4 text-center text-xs text-slate-500">
          Your phone number is used only to track your attendance record. We'll never share it.
        </div>
      </form>
    </div>
  );
}
